import api from "@modules/api/api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { vehicleValidation } from "@talladega/helpers";
import { useUppercaseAlphaNumericMask } from "@talladega/hooks";
import useFeatureFlags from "@modules/feature-flags/hooks/useFeatureFlags";
import { FeatureFlags } from "@modules/feature-flags/types/FeatureFlag";
import {
  CondensedLookupVehicle,
  VehicleLookupResponse,
} from "../types/VehicleLookupResponse";
import vehicleQueryKeys from "../vehicleQueryKeys";

const getVehicleDetails = (
  { vin, vehicleSearchString }: { vin?: string; vehicleSearchString?: string },
  key: string
): Promise<VehicleLookupResponse> => {
  const params = new URLSearchParams();
  if (vin) {
    params.append("vin", vin);
  }

  if (vehicleSearchString) {
    params.append("vehicleSearchString", vehicleSearchString.trim());
  }

  params.append("key", key);

  return api.get<VehicleLookupResponse>(
    `api/vehicles/vehicle-autocomplete?${params.toString()}`
  );
};

export function getCondensedLookupVehicleName(
  year: number,
  make: string,
  model: string,
  trim: string | null | undefined
) {
  return [year, make, model, trim].join(" ").trim();
}

export function useVehicleLookupByVin(
  vin: string,
  key: string,
  enabled?: boolean
) {
  const { maskPipe: maskUppercaseAlphaNumericPipe } =
    useUppercaseAlphaNumericMask();

  const { data, error, isFetching } = useQuery({
    queryKey: vehicleQueryKeys.vehicleLookupSearchByVin(vin),
    queryFn: () => getVehicleDetails({ vin }, key),
    enabled:
      enabled !== false &&
      vehicleValidation.vin.validVin(maskUppercaseAlphaNumericPipe(vin) ?? ""),
    staleTime: Infinity,
  });

  return {
    error,
    vehiclesFound: data?.vehicles ?? [],
    isFetching,
  };
}

export function useVinDecodeLookup(
  vin: string,
  key: string,
  enabled?: boolean
) {
  const { maskPipe: maskUppercaseAlphaNumericPipe } =
    useUppercaseAlphaNumericMask();

  const { data, error, isFetching } = useQuery({
    queryKey: vehicleQueryKeys.vehicleLookupSearchByVin(vin),
    queryFn: () => getVehicleDetails({ vin }, key),
    enabled:
      enabled !== false &&
      vehicleValidation.vin.validVin(maskUppercaseAlphaNumericPipe(vin) ?? ""),
    staleTime: Infinity,
  });

  return {
    error,
    vehiclesFound: data?.vehicles ?? [],
    isFetching,
  };
}

export function useVehicleLookupByName(
  vehicleSearchString: string,
  key: string,
  enabled = true
) {
  const minQueryLength = 3;

  const { data, error } = useQuery({
    queryKey:
      vehicleQueryKeys.vehicleLookupSearchByVehicle(vehicleSearchString),
    queryFn: () => getVehicleDetails({ vehicleSearchString }, key),
    enabled:
      !!vehicleSearchString?.trim() &&
      vehicleSearchString.length >= minQueryLength &&
      enabled,
    placeholderData: keepPreviousData,
  });

  return {
    vehicles: data?.vehicles ?? [],
    isLoading: !error && !data,
    error,
  };
}

export function useVehicleLookupByNameByUniqueTrim(
  vehicleSearchString: string,
  key: string,
  enabled = true
) {
  const { vehicles, isLoading, error } = useVehicleLookupByName(
    vehicleSearchString,
    key,
    enabled
  );

  const { isEnabled } = useFeatureFlags();
  const isRoadwiseAccuTradeVehicleLookupEnabled = isEnabled(
    FeatureFlags.RoadwiseAccuTradeVehicleLookup
  );

  const uniqueVehiclesByTrim: CondensedLookupVehicle[] = [];

  for (let i = 0; i < vehicles.length; i += 1) {
    const v = vehicles[i];
    const newName = getCondensedLookupVehicleName(
      v.year,
      v.make,
      v.model,
      isRoadwiseAccuTradeVehicleLookupEnabled ? v.style : v.trim
    );

    if (!uniqueVehiclesByTrim.some((vu) => vu.name === newName)) {
      uniqueVehiclesByTrim.push({
        year: v.year,
        make: v.make,
        model: v.model,
        trim: isRoadwiseAccuTradeVehicleLookupEnabled ? v.style : v.trim,
        name: newName,
      });
    }
  }

  return {
    vehicles: uniqueVehiclesByTrim,
    isLoading,
    error,
  };
}
